import {
  FeatureNameEnum,
  TemplateNodeSchema,
  TemplateNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import type {
  TemplateNodesDict,
  TemplatePagesDict,
} from 'src/exShared/types/types'

/**
 * ノード数ソフトリミットの標準値。
 */
export const NODE_SOFT_LIMIT_DEFAULT = 3000

/** 企業別 feature による拡張枠。上限の大きい順に適応する。 */
export const NODE_SOFT_LIMIT_EXPANSIONS: {
  feature: FeatureNameEnum
  limit: number
}[] = [
  { feature: FeatureNameEnum.REPORT_NODE_SOFT_LIMIT_20000, limit: 20000 },
  { feature: FeatureNameEnum.REPORT_NODE_SOFT_LIMIT_10000, limit: 10000 },
  { feature: FeatureNameEnum.REPORT_NODE_SOFT_LIMIT_5000, limit: 5000 },
]

/** 有効な feature から、この企業のノード数ソフトリミットを決める。 */
export const resolveNodeSoftLimit = (
  isFeatureEnabled: (feature: FeatureNameEnum) => boolean,
): number =>
  NODE_SOFT_LIMIT_EXPANSIONS.find(({ feature }) => isFeatureEnabled(feature))
    ?.limit ?? NODE_SOFT_LIMIT_DEFAULT

/** カウント対象のひな形のページとノード。 */
export type TemplateNodesForCount = {
  templatePages: TemplatePagesDict
  templatePageIds: number[]
  templateNodes: TemplateNodesDict
}

/** セクション・質問・条件分岐をノードとして数える。 */
const isCountableNode = (node: TemplateNodeSchema): boolean =>
  node.type === TemplateNodeTypeEnum.Question ||
  node.type === TemplateNodeTypeEnum.Logic ||
  node.type === TemplateNodeTypeEnum.Section

/** 指定ノード群とその子孫のカウント対象ノード数。 */
const countNodesInSubtree = (
  nodeIds: number[],
  templateNodes: TemplateNodesDict,
): number =>
  nodeIds.reduce((count, nodeId) => {
    const node = templateNodes[nodeId]
    if (!node) {
      return count
    }
    return (
      count +
      (isCountableNode(node) ? 1 : 0) +
      countNodesInSubtree(node.nodes, templateNodes)
    )
  }, 0)

/**
 * ひな形全体のノード数。全ページから到達できるセクション・質問・条件分岐を数える。
 */
export const countTemplateNodes = ({
  templatePages,
  templatePageIds,
  templateNodes,
}: TemplateNodesForCount): number =>
  templatePageIds.reduce(
    (count, pageId) =>
      count +
      countNodesInSubtree(templatePages[pageId]?.nodes ?? [], templateNodes),
    0,
  )
